import { createApi } from '@reduxjs/toolkit/query/react'
import { notificationFeedScopeTags, notificationsApi, notificationsTag } from './notifications'
import { recommendationsApi, recommendationsSummaryTag, recommendationsTag } from './recommendations'
import { original } from '@reduxjs/toolkit'
import { patchCommentNodePreserveChildren } from './commentTreePatch'
import { BACKEND_API_URL, CURSOR_LIST_PAGE_SIZE, urlApi } from '../consts/api'
import { appendCommentsSortQueryParams, DEFAULT_COMMENTS_SORT } from '../components/CommentsSection/helpers/commentsSortQuery'

import { extractResponseData, extractResponseError, fetchBaseQueryWithToken } from '../utils/api'
import { getMediaSourceUrl } from '../utils/media'
import type { MediaResourceLike } from '../utils/media'
import {
    IAddCommentToProject,
    IUpdateCommentBody,
    ISetCommentReactionBody,
    IGetCommentResponse,
    ICreateProject,
    ICreateProjectStepBody,
    IUpdateProjectStepBody,
    IProjectResourceResponse,
    IProjectResponse,
    IProjectStepResource,
    IUpdateProjectDescriptionBody,
    TProjectsListResponse,
    TGetCommentsResponse,
    TGetProjectsQueryParams,
    GetCommentsQueryArg,
    IProjectLikeUserResource,
    TProjectsCursorPage,
    TGetProjectsCursorQueryParams,
    TProjectsStatsResponse,
    IProjectsListItem,
    CommentsSortUi,
    IProjectTeamMemberResource,
    IProjectTeamPreviewItem,
    IProjectAuthorDisplay,
    IProjectModerationUserCandidate,
    IProjectModerationUserCandidatesResponse,
    IUpdateProjectTeamBody,
    IUpdateProjectAdminsBody,
    IProjectUserRestrictionResource,
    ProjectUserRestrictionLevel,
    IProjectViewerPermissions,
    IProjectTeamSpaceResource,
    IProjectTeamBoardPostResource,
    IProjectTeamInvitationResource,
    IProjectParticipatingTeam,
    IProjectSourceSocialOrder,
    IProjectMemberSourceTeam,
    IProjectNeedSlotMutationBody,
    IProjectNeedSlotResource,
} from './types'

export const projectsTag = 'projectsTag' as const
const LIKES_PREVIEW_MAX = 3

const PROJECT_LIST_ENDPOINTS = new Set([
    'getProjects',
    'getProjectsPage',
    'getMyProjects',
    'getMyProjectsPage',
    'getSubscribedProjects',
    'getSubscribedProjectsPage',
    'getParticipatingProjects',
    'getParticipatingProjectsPage',
    'getUserParticipatingProjectsPage',
])

type TProjectLikeCountersDraft = {
    viewer?: { is_liked?: boolean } | null
    counters: { likes?: number }
}

function applyProjectLikeToggle(draft: TProjectLikeCountersDraft): void {
    const wasLiked = draft.viewer?.is_liked === true
    if (draft.viewer) {
        draft.viewer.is_liked = !wasLiked
    }
    draft.counters.likes = Math.max(0, Number(draft.counters.likes ?? 0) + (wasLiked ? -1 : 1))
}

function projectListProvidesTags(
    result: TProjectsListResponse | TProjectsCursorPage | undefined
): Array<{ type: typeof projectsTag; id: string }> {
    if (!result) {
        return [{ type: projectsTag, id: 'LIST' }]
    }
    const items = Array.isArray(result) ? result : result.items ?? []
    return [
        { type: projectsTag, id: 'LIST' },
        ...items.map((p) => ({ type: projectsTag, id: `id:${p.id}` })),
    ]
}

function patchProjectInListCaches(
    dispatch: (action: unknown) => { undo: () => void },
    getState: () => unknown,
    projectId: number,
    patcher: (draft: IProjectsListItem) => void
): Array<{ undo: () => void }> {
    const patches: Array<{ undo: () => void }> = []
    const state = getState() as {
        projectsApi?: {
            queries?: Record<
                string,
                {
                    endpointName?: string
                    originalArgs?: unknown
                    data?: unknown
                }
            >
        }
    }
    const queries = state.projectsApi?.queries ?? {}

    for (const queryEntry of Object.values(queries)) {
        const endpointName = queryEntry?.endpointName
        if (!endpointName || !PROJECT_LIST_ENDPOINTS.has(endpointName) || queryEntry.data == null) {
            continue
        }

        const args = queryEntry.originalArgs

        if (endpointName.endsWith('Page')) {
            const patch = dispatch(
                projectsApi.util.updateQueryData(
                    endpointName as 'getProjectsPage',
                    args as TGetProjectsCursorQueryParams | void,
                    (draft) => {
                        const item = draft.items?.find((p) => p.id === projectId)
                        if (item) {
                            patcher(item)
                        }
                    }
                )
            )
            patches.push(patch)
            continue
        }

        const patch = dispatch(
            projectsApi.util.updateQueryData(
                endpointName as 'getProjects',
                args as TGetProjectsQueryParams | void,
                (draft) => {
                    const item = draft.find((p) => p.id === projectId)
                    if (item) {
                        patcher(item)
                    }
                }
            )
        )
        patches.push(patch)
    }

    return patches
}

export type TProjectMutationCacheArg = {
    projectId: number
    slug?: string
}

type TTrackProjectViewResponse = {
    views: number
}

function projectListInvalidationTags(projectId: number): Array<{ type: typeof projectsTag; id: string }> {
    return [
        { type: projectsTag, id: 'LIST' },
        { type: projectsTag, id: `id:${projectId}` },
    ]
}

function resolveSourceSocialOrderId(
    value: number | null | undefined,
    source: IProjectSourceSocialOrder | null | undefined,
): number | null {
    const fromValue = Number(value)
    if (Number.isFinite(fromValue) && fromValue > 0) return fromValue
    const fromSource = Number(source?.id)
    if (Number.isFinite(fromSource) && fromSource > 0) return fromSource
    return null
}

function getCachedProjectSourceSocialOrderId(
    getState: () => unknown,
    projectId: number,
): number | null {
    const state = getState() as {
        projectsApi?: {
            queries?: Record<
                string,
                {
                    endpointName?: string
                    data?: unknown
                }
            >
        }
    }
    const queries = state.projectsApi?.queries ?? {}
    for (const entry of Object.values(queries)) {
        if (entry?.endpointName !== 'getProject' || entry.data == null) continue
        const data = entry.data as IProjectResponse
        if (Number(data.id) !== projectId) continue
        return resolveSourceSocialOrderId(null, data.source_social_order)
    }
    return null
}

async function invalidateLinkedSocialOrderProjects(
    dispatch: (action: unknown) => unknown,
    socialOrderId: number | null | undefined,
): Promise<void> {
    const id = resolveSourceSocialOrderId(socialOrderId, null)
    if (id == null) return
    // Dynamic import avoids projects ↔ socialOrders circular module init.
    const { invalidateSocialOrderLinkedProjectsCache } = await import('./socialOrders')
    invalidateSocialOrderLinkedProjectsCache(dispatch, id)
}

/** Лайк/unlike: только LIST — не инвалидировать getProject по id (optimistic patch + гонка refetch). */
function projectLikeListInvalidationTags(): Array<{ type: typeof projectsTag; id: string }> {
    return [{ type: projectsTag, id: 'LIST' }]
}

function patchGetProjectDetail(
    dispatch: (action: unknown) => { undo: () => void },
    slug: string | undefined,
    patcher: (draft: IProjectResponse) => void
): { undo: () => void } | null {
    if (!slug) {
        return null
    }

    return dispatch(
        projectsApi.util.updateQueryData('getProject', slug, (draft) => {
            patcher(draft)
        })
    ) as { undo: () => void }
}

function normalizeUserShort(raw: any): any {
    if (!raw || typeof raw !== 'object') return raw
    const avatarRaw = raw?.avatar ?? raw?.avatar_url ?? raw?.avatarUrl
    return {
        ...raw,
        avatar: getMediaSourceUrl(avatarRaw),
    }
}

function normalizeTeamBoardPost(item: any): IProjectTeamBoardPostResource | null {
    const userId = Number(item?.user_id ?? item?.user?.id)
    if (!Number.isSafeInteger(userId) || userId <= 0) return null
    const body = typeof item?.body === 'string' ? item.body : ''
    const telegramRaw = typeof item?.telegram === 'string' ? item.telegram.trim() : ''
    const user = normalizeUserShort(item?.user) ?? { id: userId, slug: null }
    return {
        user,
        user_id: userId,
        body,
        telegram: telegramRaw.length > 0 ? telegramRaw : null,
        updated_at: String(item?.updated_at ?? ''),
        created_at: String(item?.created_at ?? ''),
    } satisfies IProjectTeamBoardPostResource
}

function normalizeCommentMyReaction(raw: unknown): number | null | undefined {
    if (raw === undefined) return undefined
    if (raw === null) return null
    if (raw === 'like') return 1
    if (raw === 'dislike') return -1
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
}

/** Дерево комментариев: автор как UserShortResource — avatar объектом `{ id, sizes }`, без этого MUI Avatar не показывает картинку. */
function normalizeCommentNode(raw: unknown): IGetCommentResponse {
    if (raw == null || typeof raw !== 'object') {
        return raw as IGetCommentResponse
    }
    const c = raw as Record<string, unknown>
    const childrenRaw = c.children

    return {
        ...c,
        author: normalizeUserShort(c.author),
        my_reaction: normalizeCommentMyReaction(c.my_reaction),
        viewer: c.viewer && typeof c.viewer === 'object'
            ? { can_delete: (c.viewer as Record<string, unknown>).can_delete === true }
            : undefined,
        children: Array.isArray(childrenRaw)
            ? (childrenRaw as unknown[]).map((child) => normalizeCommentNode(child))
            : (childrenRaw as IGetCommentResponse['children']),
    } as IGetCommentResponse
}

function normalizeLikesPreview(raw: any): any[] | null {
    const list = raw?.likes_preview
    if (!Array.isArray(list)) return null
    return list.map((u: any) => normalizeUserShort(u))
}

function normalizeTeamPreview(raw: any): IProjectTeamPreviewItem[] | null {
    const list = raw?.team_preview
    if (!Array.isArray(list)) return null

    return list
        .map((item: any) => {
            const user = normalizeUserShort(item?.user)
            if (!user || typeof user !== 'object') return null
            const teamRole = String(item?.team_role ?? (item?.is_author ? 'author' : 'member'))
            return {
                user,
                team_role: (teamRole === 'author' || teamRole === 'coauthor' || teamRole === 'member'
                    ? teamRole
                    : 'member') as IProjectTeamPreviewItem['team_role'],
                role_label: item?.role_label != null ? String(item.role_label) : null,
                is_author: Boolean(item?.is_author),
            } satisfies IProjectTeamPreviewItem
        })
        .filter((item: IProjectTeamPreviewItem | null): item is IProjectTeamPreviewItem => item !== null)
}

function normalizeAuthorDisplay(raw: any): IProjectAuthorDisplay | null {
    const source = raw?.author_display
    if (!source || typeof source !== 'object') return null
    return {
        role_label: source.role_label != null ? String(source.role_label) : null,
        is_featured: Boolean(source.is_featured ?? true),
        sort_order: Number(source.sort_order ?? 0),
    }
}

function normalizeParticipatingTeams(raw: unknown): IProjectParticipatingTeam[] | null {
    if (!Array.isArray(raw)) return null
    const items: IProjectParticipatingTeam[] = []
    for (const entry of raw) {
        const item = entry as Record<string, unknown>
        const id = Number(item?.id)
        if (!Number.isFinite(id) || id <= 0) continue
        const countersRaw = item?.counters
        items.push({
            id,
            slug: String(item?.slug ?? ''),
            name: String(item?.name ?? ''),
            slogan: item?.slogan != null ? String(item.slogan) : null,
            image: getMediaSourceUrl(item?.image) ?? null,
            counters:
                countersRaw && typeof countersRaw === 'object'
                    ? {
                          members: Number((countersRaw as Record<string, unknown>).members ?? 0),
                          views: Number((countersRaw as Record<string, unknown>).views ?? 0),
                          subscribers: Number((countersRaw as Record<string, unknown>).subscribers ?? 0),
                      }
                    : undefined,
        })
    }
    return items
}

function normalizeSourceTeam(raw: unknown): IProjectMemberSourceTeam | null {
    if (!raw || typeof raw !== 'object') return null
    const item = raw as Record<string, unknown>
    const id = Number(item.id)
    if (!Number.isFinite(id) || id <= 0) return null
    return {
        id,
        slug: String(item.slug ?? ''),
        name: String(item.name ?? ''),
    }
}

export function parseProjectTeamInvitation(raw: unknown): IProjectTeamInvitationResource | null {
    if (!raw || typeof raw !== 'object') return null
    const item = raw as Record<string, unknown>
    const id = Number(item.id)
    if (!Number.isFinite(id) || id <= 0) return null
    const projectRaw = item.project
    const teamRaw = item.team
    const status = String(item.status ?? 'pending') as IProjectTeamInvitationResource['status']
    return {
        id,
        project_id: Number(item.project_id ?? (projectRaw as Record<string, unknown> | undefined)?.id ?? 0),
        project:
            projectRaw && typeof projectRaw === 'object'
                ? {
                      id: Number((projectRaw as Record<string, unknown>).id),
                      name: String((projectRaw as Record<string, unknown>).name ?? ''),
                      slug: String((projectRaw as Record<string, unknown>).slug ?? ''),
                      image: getMediaSourceUrl((projectRaw as Record<string, unknown>).image) ?? null,
                  }
                : null,
        team_id: Number(item.team_id ?? (teamRaw as Record<string, unknown> | undefined)?.id ?? 0),
        team:
            teamRaw && typeof teamRaw === 'object'
                ? {
                      id: Number((teamRaw as Record<string, unknown>).id),
                      name: String((teamRaw as Record<string, unknown>).name ?? ''),
                      slug: String((teamRaw as Record<string, unknown>).slug ?? ''),
                      image: getMediaSourceUrl((teamRaw as Record<string, unknown>).image) ?? null,
                  }
                : null,
        inviter: item.inviter ? normalizeUserShort(item.inviter) : null,
        status,
        message: item.message != null ? String(item.message) : null,
        response_message: item.response_message != null ? String(item.response_message) : null,
        expires_at: item.expires_at != null ? String(item.expires_at) : null,
        decided_at: item.decided_at != null ? String(item.decided_at) : null,
        created_at: String(item.created_at ?? ''),
        updated_at: String(item.updated_at ?? ''),
    }
}

export function parseProjectTeamInvitationsList(response: unknown): IProjectTeamInvitationResource[] {
    const payload = extractResponseData(response)
    if (!Array.isArray(payload)) return []
    return payload
        .map((item) => parseProjectTeamInvitation(item))
        .filter((item): item is IProjectTeamInvitationResource => item !== null)
}

function parseProjectsListResponse(response: unknown): TProjectsListResponse {
    const payload = extractResponseData(response)
    if (Array.isArray(payload)) {
        return (payload as TProjectsListResponse).map((p) => ({
            ...p,
            image: getMediaSourceUrl((p as any).image),
            author: (() => {
                const rawAuthor: any = (p as any).author ?? null
                if (!rawAuthor) return rawAuthor
                return normalizeUserShort(rawAuthor)
            })(),
        }))
    }
    if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
        return ((payload as { data: TProjectsListResponse }).data).map((p) => ({
            ...p,
            image: getMediaSourceUrl((p as any).image),
            author: (() => {
                const rawAuthor: any = (p as any).author ?? null
                if (!rawAuthor) return rawAuthor
                return normalizeUserShort(rawAuthor)
            })(),
        }))
    }
    return []
}

function pickCursorFromNextLink(next: string): string | null {
    try {
        const fromUrl = new URL(next, BACKEND_API_URL).searchParams.get('cursor')
        if (fromUrl) {
            return fromUrl
        }
    } catch {
        // fall through to regex
    }

    const match = next.match(/[?&]cursor=([^&]+)/)
    return match?.[1] ? decodeURIComponent(match[1]) : null
}

function pickNextCursorFromPageRoot(body: Record<string, unknown>): string | null {
    const meta = body.meta as Record<string, unknown> | undefined
    const fromMeta = meta?.next_cursor
    if (fromMeta !== undefined && fromMeta !== null && String(fromMeta) !== '') {
        return String(fromMeta)
    }

    const links = body.links as Record<string, unknown> | undefined
    const next = links?.next
    if (typeof next === 'string' && next.length > 0) {
        return pickCursorFromNextLink(next)
    }

    return null
}

export function parseProjectsCursorPageResponse(response: unknown): TProjectsCursorPage {
    const top = (response ?? {}) as Record<string, unknown>
    const data = top.data

    if (Array.isArray(data)) {
        return {
            items: parseProjectsListResponse(response),
            meta: { next_cursor: pickNextCursorFromPageRoot(top) },
        }
    }

    if (data && typeof data === 'object' && Array.isArray((data as Record<string, unknown>).data)) {
        const root = data as Record<string, unknown>
        return {
            items: parseProjectsListResponse(response),
            meta: { next_cursor: pickNextCursorFromPageRoot(root) },
        }
    }

    return {
        items: parseProjectsListResponse(response),
        meta: { next_cursor: null },
    }
}

function serializeProjectsQueryParams(params: TGetProjectsCursorQueryParams | void): Record<string, string> {
    const raw = params ?? {}
    const result: Record<string, string> = {}
    const q = raw.query?.trim()
    if (q) {
        result.query = q
    }
    const type = raw.type?.trim()
    if (type && type !== 'all') {
        result.type = type
    }
    const stage = raw.stage?.trim()
    if (stage && stage !== 'all') {
        result.stage = stage
    }
    const tag = raw.tag?.trim()
    if (tag) {
        result.tag = tag
    }
    if (raw.cursor) {
        result.cursor = raw.cursor
    }
    if (typeof raw.limit === 'number' && Number.isFinite(raw.limit)) {
        result.limit = String(raw.limit)
    }
    if (raw.sort_by) {
        result.sort_by = raw.sort_by
    }
    if (raw.sort_direction) {
        result.sort_direction = raw.sort_direction
    }
    return result
}

function normalizeViewerPermissions(raw: unknown): IProjectViewerPermissions | undefined {
    if (raw == null || typeof raw !== 'object') return undefined
    const permissions = (raw as Record<string, unknown>).permissions
    if (permissions == null || typeof permissions !== 'object') return undefined
    const p = permissions as Record<string, unknown>
    const level = p.restriction_level
    return {
        is_owner: Boolean(p.is_owner),
        can_edit_content: Boolean(p.can_edit_content),
        can_moderate: Boolean(p.can_moderate),
        can_manage_admins: Boolean(p.can_manage_admins),
        can_manage_team: Boolean(p.can_manage_team),
        can_delete_project: Boolean(p.can_delete_project),
        can_comment: p.can_comment !== false,
        can_view_team_space: Boolean(p.can_view_team_space),
        restriction_level:
            level === 'full' || level === 'comments' ? level : null,
    }
}

function normalizeProjectViewer(raw: unknown): IProjectResponse['viewer'] {
    if (raw == null || typeof raw !== 'object') return null
    const v = raw as Record<string, unknown>
    const permissions = normalizeViewerPermissions(v)
    return {
        is_followed: Boolean(v.is_followed),
        is_joined: Boolean(v.is_joined),
        is_liked: v.is_liked === true,
        ...(permissions ? { permissions } : {}),
    }
}

function normalizeSourceSocialOrder(raw: unknown): IProjectSourceSocialOrder | null {
    if (raw == null || typeof raw !== 'object') return null
    const item = raw as Record<string, unknown>
    const id = Number(item.id)
    if (!Number.isFinite(id) || id <= 0) return null
    return {
        id,
        title: String(item.title ?? ''),
        slug: String(item.slug ?? ''),
        image: getMediaSourceUrl(item.image) ?? null,
    }
}

function parseProjectResponse(response: unknown): IProjectResponse {
    const p = extractResponseData(response) as IProjectResponse
    const rawAuthor: any = (p as any).author ?? null
    const likesPreview = normalizeLikesPreview(p as any)
    const teamPreview = normalizeTeamPreview(p as any)
    const participatingTeams = normalizeParticipatingTeams((p as any).participating_teams)
    const sourceSocialOrder = normalizeSourceSocialOrder((p as any).source_social_order)
    const authorDisplay = normalizeAuthorDisplay(p as any)
    const viewer = normalizeProjectViewer((p as any).viewer)

    return {
        ...p,
        image: getMediaSourceUrl((p as any).image),
        image_gallery: Array.isArray((p as any).image_gallery)
            ? ((p as any).image_gallery as unknown[]).map((x) => getMediaSourceUrl(x)).filter(Boolean) as string[]
            : null,
        author: normalizeUserShort(rawAuthor),
        likes_preview: likesPreview ?? (p as any).likes_preview,
        team_preview: teamPreview ?? (p as any).team_preview,
        participating_teams: participatingTeams ?? (p as any).participating_teams ?? null,
        source_social_order: sourceSocialOrder,
        author_display: authorDisplay ?? (p as any).author_display ?? null,
        viewer: viewer ?? (p as any).viewer ?? null,
    }
}

/**
 * Ответ индекса комментариев — массив или обёртка (в т.ч. первая страница Laravel cursor).
 * TODO: при поддержке cursor «следующая страница» для parent_id — политика слияния вместо одной замены.
 */
function parseCommentsListResponse(response: unknown): TGetCommentsResponse {
    const payload = extractResponseData(response)
    if (Array.isArray(payload)) {
        return payload.map((item) => normalizeCommentNode(item))
    }
    if (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)) {
        return (payload as { data: unknown[] }).data.map((item) => normalizeCommentNode(item))
    }
    return []
}

/** Страница GET с parent_id (cursor); meta не теряем, в отличие от parseCommentsListResponse. */
export type TCommentThreadPage = {
    items: IGetCommentResponse[]
    nextCursor: string | null
}

export type GetCommentThreadPageArg = {
    projectId: number
    type: string
    parentId: number
    cursor?: string | null
    limit?: number
}

/**
 * GET комментариев: ищем массив записей и объект, где лежит meta.next_cursor (Laravel cursor).
 * Учитываем обёртку `{ data: { data: [], meta, links } }` как у части эндпоинтов.
 */
function extractCommentThreadRowsAndMetaRoot(body: unknown): {
    rows: unknown[]
    metaRoot: Record<string, unknown>
} {
    if (body === null || typeof body !== 'object') {
        return { rows: [], metaRoot: {} }
    }
    const top = body as Record<string, unknown>

    if (Array.isArray(top.data)) {
        return { rows: top.data as unknown[], metaRoot: top }
    }

    const inner = top.data
    if (inner !== null && typeof inner === 'object' && !Array.isArray(inner)) {
        const lr = inner as Record<string, unknown>
        if (Array.isArray(lr.data)) {
            return { rows: lr.data as unknown[], metaRoot: lr }
        }
    }

    const once = extractResponseData(body)
    if (Array.isArray(once)) {
        return { rows: once, metaRoot: top }
    }
    if (once && typeof once === 'object' && Array.isArray((once as Record<string, unknown>).data)) {
        const o = once as Record<string, unknown>
        return { rows: o.data as unknown[], metaRoot: o }
    }

    return { rows: [], metaRoot: top }
}

function pickNextCursorFromBody(body: Record<string, unknown>): string | null {
    const meta = body.meta as Record<string, unknown> | undefined
    const fromMeta =
        meta?.next_cursor !== undefined && meta?.next_cursor !== null && meta.next_cursor !== ''
            ? String(meta.next_cursor)
            : ''
    if (fromMeta.length > 0) return fromMeta
    const links = body.links as Record<string, unknown> | undefined
    const next = links?.next
    if (typeof next === 'string' && next.length > 0) {
        try {
            const u = new URL(next, BACKEND_API_URL)
            const c = u.searchParams.get('cursor')
            return c ?? null
        } catch {
            return null
        }
    }
    return null
}

function parseProjectLikesResponse(
    response: unknown
): { data: IProjectLikeUserResource[]; meta: { next_cursor: string | null } } {
    const raw = response as any
    const data = Array.isArray(raw?.data) ? (raw.data as any[]) : []
    const meta = raw?.meta && typeof raw.meta === 'object' ? raw.meta : {}

    return {
        data: data.map((u) => {
            const rawAvatar = (u as any)?.avatar
            return {
                ...u,
                avatar: getMediaSourceUrl(rawAvatar),
            } as IProjectLikeUserResource
        }),
        meta: {
            next_cursor: (meta as any).next_cursor ?? null,
        },
    }
}

export const projectsApi = createApi({
    reducerPath: 'projectsApi',
    baseQuery: fetchBaseQueryWithToken(BACKEND_API_URL),
    tagTypes: [projectsTag],
    endpoints: (builder) => ({
        getProjects: builder.query<TProjectsListResponse, TGetProjectsQueryParams | void>({
            query: (arg) => {
                const serialized = serializeProjectsQueryParams(arg)
                return {
                    url: `/${urlApi.projects}`,
                    ...(Object.keys(serialized).length > 0 ? { params: serialized } : {}),
                }
            },
            transformResponse: (response: unknown) => parseProjectsListResponse(response),
            transformErrorResponse: extractResponseError,
            providesTags: (result) => projectListProvidesTags(result),
        }),
        getProjectsPage: builder.query<TProjectsCursorPage, TGetProjectsCursorQueryParams | void>({
            query: (arg) => {
                const serialized = serializeProjectsQueryParams(arg)
                return {
                    url: `/${urlApi.projects}`,
                    ...(Object.keys(serialized).length > 0 ? { params: serialized } : {}),
                }
            },
            transformResponse: (response: unknown) => parseProjectsCursorPageResponse(response),
            transformErrorResponse: extractResponseError,
            providesTags: (result) => projectListProvidesTags(result),
        }),

        getMyProjects: builder.query<TProjectsListResponse, void>({
            query: () => ({
                url: `/${urlApi.projects}/my`,
            }),
            transformResponse: (response: unknown) => parseProjectsListResponse(response),
            transformErrorResponse: extractResponseError,
            providesTags: (result) => projectListProvidesTags(result),
        }),
        getMyProjectsPage: builder.query<TProjectsCursorPage, TGetProjectsCursorQueryParams | void>({
            query: (arg) => {
                const serialized = serializeProjectsQueryParams(arg)
                return {
                    url: `/${urlApi.projects}/my`,
                    ...(Object.keys(serialized).length > 0 ? { params: serialized } : {}),
                }
            },
            transformResponse: (response: unknown) => parseProjectsCursorPageResponse(response),
            transformErrorResponse: extractResponseError,
            providesTags: (result) => projectListProvidesTags(result),
        }),

        getSubscribedProjects: builder.query<TProjectsListResponse, void>({
            query: () => ({
                url: `/users/me/subscriptions/projects`,
            }),
            transformResponse: (response: unknown) => parseProjectsListResponse(response),
            transformErrorResponse: extractResponseError,
            providesTags: (result) => projectListProvidesTags(result),
        }),
        getSubscribedProjectsPage: builder.query<TProjectsCursorPage, TGetProjectsCursorQueryParams | void>({
            query: (arg) => {
                const serialized = serializeProjectsQueryParams(arg)
                return {
                    url: `/users/me/subscriptions/projects`,
                    ...(Object.keys(serialized).length > 0 ? { params: serialized } : {}),
                }
            },
            transformResponse: (response: unknown) => parseProjectsCursorPageResponse(response),
            transformErrorResponse: extractResponseError,
            providesTags: (result) => projectListProvidesTags(result),
        }),

        getParticipatingProjects: builder.query<TProjectsListResponse, void>({
            query: () => ({
                url: `/${urlApi.projects}/participating`,
            }),
            transformResponse: (response: unknown) => parseProjectsListResponse(response),
            transformErrorResponse: extractResponseError,
            providesTags: (result) => projectListProvidesTags(result),
        }),
        getParticipatingProjectsPage: builder.query<TProjectsCursorPage, TGetProjectsCursorQueryParams | void>({
            query: (arg) => {
                const serialized = serializeProjectsQueryParams(arg)
                return {
                    url: `/${urlApi.projects}/participating`,
                    ...(Object.keys(serialized).length > 0 ? { params: serialized } : {}),
                }
            },
            transformResponse: (response: unknown) => parseProjectsCursorPageResponse(response),
            transformErrorResponse: extractResponseError,
            providesTags: (result) => projectListProvidesTags(result),
        }),
        getUserParticipatingProjectsPage: builder.query<
            TProjectsCursorPage,
            TGetProjectsCursorQueryParams & { userId: number }
        >({
            query: ({ userId, ...arg }) => {
                const serialized = serializeProjectsQueryParams(arg)
                return {
                    url: `/${urlApi.users}/${userId}/projects/participating`,
                    ...(Object.keys(serialized).length > 0 ? { params: serialized } : {}),
                }
            },
            transformResponse: (response: unknown) => parseProjectsCursorPageResponse(response),
            transformErrorResponse: extractResponseError,
            providesTags: (result) => projectListProvidesTags(result),
        }),

        getProjectsStats: builder.query<TProjectsStatsResponse, void>({
            query: () => ({
                url: `/${urlApi.projects}/stats`,
            }),
            transformResponse: (response: unknown) => {
                const data = extractResponseData(response) as Partial<TProjectsStatsResponse> | null
                return {
                    projects_total_count: Number(data?.projects_total_count ?? 0),
                    projects_published_count: Number(data?.projects_published_count ?? 0),
                    projects_unpublished_count: Number(data?.projects_unpublished_count ?? 0),
                }
            },
            transformErrorResponse: extractResponseError,
            providesTags: [projectsTag, { type: projectsTag, id: 'STATS' }],
        }),

        trackProjectView: builder.mutation<TTrackProjectViewResponse, { projectId: number }>({
            query: ({ projectId }) => ({
                url: `/${urlApi.projects}/${projectId}/view`,
                method: 'POST',
            }),
            transformResponse: (response: unknown) => {
                const data = extractResponseData(response) as { views?: number } | null
                return { views: Number(data?.views ?? 0) }
            },
            transformErrorResponse: extractResponseError,
        }),

        subscribeProject: builder.mutation<unknown, TProjectMutationCacheArg>({
            query: ({ projectId }) => ({
                url: `/${urlApi.projects}/${projectId}/subscribe`,
                method: 'POST',
            }),
            transformResponse: (response: unknown) => extractResponseData(response),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => projectListInvalidationTags(arg.projectId),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                const patch = patchGetProjectDetail(dispatch, arg.slug, (draft) => {
                    if (draft.viewer) {
                        draft.viewer.is_followed = true
                    }
                    draft.is_followed = true
                    draft.counters.subscribers = Number(draft.counters.subscribers ?? 0) + 1
                })
                try {
                    await queryFulfilled
                } catch {
                    patch?.undo()
                }
            },
        }),

        unsubscribeProject: builder.mutation<unknown, TProjectMutationCacheArg>({
            query: ({ projectId }) => ({
                url: `/${urlApi.projects}/${projectId}/subscribe`,
                method: 'DELETE',
            }),
            transformResponse: (response: unknown) => extractResponseData(response),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => projectListInvalidationTags(arg.projectId),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                const patch = patchGetProjectDetail(dispatch, arg.slug, (draft) => {
                    if (draft.viewer) {
                        draft.viewer.is_followed = false
                    }
                    draft.is_followed = false
                    draft.counters.subscribers = Math.max(0, Number(draft.counters.subscribers ?? 0) - 1)
                })
                try {
                    await queryFulfilled
                } catch {
                    patch?.undo()
                }
            },
        }),

        likeProject: builder.mutation<unknown, TProjectMutationCacheArg>({
            query: ({ projectId }) => ({
                url: `/${urlApi.projects}/${projectId}/like`,
                method: 'POST',
            }),
            transformResponse: (response: unknown) => extractResponseData(response),
            transformErrorResponse: extractResponseError,
            invalidatesTags: () => projectLikeListInvalidationTags(),
            async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
                const patch = patchGetProjectDetail(dispatch, arg.slug, applyProjectLikeToggle)
                const listPatches = patchProjectInListCaches(dispatch, getState, arg.projectId, applyProjectLikeToggle)
                try {
                    await queryFulfilled
                    if (arg.slug) {
                        const likesPreviewResult = await dispatch(
                            projectsApi.endpoints.getProjectLikes.initiate(
                                { projectId: arg.projectId, limit: LIKES_PREVIEW_MAX, cursor: null },
                                { forceRefetch: true }
                            )
                        ).unwrap()
                        patchGetProjectDetail(dispatch, arg.slug, (draft) => {
                            draft.likes_preview = (likesPreviewResult.data ?? [])
                                .slice(0, LIKES_PREVIEW_MAX)
                                .map((user) => ({
                                    id: user.id,
                                    slug: user.slug ?? null,
                                    shortname: user.shortname ?? null,
                                    fullname: user.fullname ?? null,
                                    avatar: user.avatar ?? null,
                                    deleted: user.deleted ?? false,
                                }))
                        })
                    }
                } catch {
                    patch?.undo()
                    listPatches.forEach((listPatch) => listPatch.undo())
                }
            },
        }),

        unlikeProject: builder.mutation<unknown, TProjectMutationCacheArg>({
            query: ({ projectId }) => ({
                url: `/${urlApi.projects}/${projectId}/like`,
                method: 'DELETE',
            }),
            transformResponse: (response: unknown) => extractResponseData(response),
            transformErrorResponse: extractResponseError,
            invalidatesTags: () => projectLikeListInvalidationTags(),
            async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
                const patch = patchGetProjectDetail(dispatch, arg.slug, applyProjectLikeToggle)
                const listPatches = patchProjectInListCaches(dispatch, getState, arg.projectId, applyProjectLikeToggle)
                try {
                    await queryFulfilled
                    if (arg.slug) {
                        const likesPreviewResult = await dispatch(
                            projectsApi.endpoints.getProjectLikes.initiate(
                                { projectId: arg.projectId, limit: LIKES_PREVIEW_MAX, cursor: null },
                                { forceRefetch: true }
                            )
                        ).unwrap()
                        patchGetProjectDetail(dispatch, arg.slug, (draft) => {
                            draft.likes_preview = (likesPreviewResult.data ?? [])
                                .slice(0, LIKES_PREVIEW_MAX)
                                .map((user) => ({
                                    id: user.id,
                                    slug: user.slug ?? null,
                                    shortname: user.shortname ?? null,
                                    fullname: user.fullname ?? null,
                                    avatar: user.avatar ?? null,
                                    deleted: user.deleted ?? false,
                                }))
                        })
                    }
                } catch {
                    patch?.undo()
                    listPatches.forEach((listPatch) => listPatch.undo())
                }
            },
        }),

        getProjectLikes: builder.query<
            { data: IProjectLikeUserResource[]; meta: { next_cursor: string | null } },
            { projectId: number; limit?: number; cursor?: string | null }
        >({
            query: ({ projectId, limit, cursor }) => ({
                url: `/${urlApi.projects}/${projectId}/likes`,
                params: {
                    ...(limit != null ? { limit: String(limit) } : {}),
                    ...(cursor ? { cursor } : {}),
                },
            }),
            // IMPORTANT: keep meta.next_cursor (extractResponseData would drop it)
            transformResponse: (response: unknown) => parseProjectLikesResponse(response),
            transformErrorResponse: extractResponseError,
            providesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }],
        }),

        createProjectJoinRequest: builder.mutation<unknown, { projectId: number; message?: string }>({
            query: ({ projectId, message }) => ({
                url: `/${urlApi.projects}/${projectId}/join-requests`,
                method: 'POST',
                ...(typeof message === 'string' && message.trim().length > 0 ? { body: { message: message.trim() } } : {}),
            }),
            transformResponse: (response: unknown) => extractResponseData(response),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }, projectsTag],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    dispatch(recommendationsApi.util.invalidateTags([recommendationsTag, recommendationsSummaryTag]))
                } catch {
                    // ignore
                }
            },
        }),

        approveProjectJoinRequest: builder.mutation<
            unknown,
            { projectId: number; joinRequestId: number; decision_message?: string }
        >({
            query: ({ projectId, joinRequestId, decision_message }) => ({
                url: `/${urlApi.projects}/${projectId}/join-requests/${joinRequestId}/approve`,
                method: 'POST',
                body: decision_message != null ? { decision_message } : {},
            }),
            transformResponse: (response: unknown) => extractResponseData(response),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }, projectsTag],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    dispatch(
                        notificationsApi.util.invalidateTags([
                            ...notificationFeedScopeTags,
                            { type: notificationsTag },
                        ])
                    )
                } catch {
                    // ignore
                }
            },
        }),

        rejectProjectJoinRequest: builder.mutation<
            unknown,
            { projectId: number; joinRequestId: number; decision_message?: string }
        >({
            query: ({ projectId, joinRequestId, decision_message }) => ({
                url: `/${urlApi.projects}/${projectId}/join-requests/${joinRequestId}/reject`,
                method: 'POST',
                body: decision_message != null ? { decision_message } : {},
            }),
            transformResponse: (response: unknown) => extractResponseData(response),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }, projectsTag],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    dispatch(
                        notificationsApi.util.invalidateTags([
                            ...notificationFeedScopeTags,
                            { type: notificationsTag },
                        ])
                    )
                } catch {
                    // ignore
                }
            },
        }),

        getInvitableProjects: builder.query<TProjectsListResponse, void>({
            query: () => ({
                url: `/users/me/projects/invitable`,
            }),
            transformResponse: (response: unknown) => parseProjectsListResponse(response),
            transformErrorResponse: extractResponseError,
            providesTags: [{ type: projectsTag, id: 'INVITABLE' }],
        }),

        createProjectInvitation: builder.mutation<
            unknown,
            { projectId: number; inviteeId: number; message?: string }
        >({
            query: ({ projectId, inviteeId, message }) => ({
                url: `/${urlApi.projects}/${projectId}/invitations`,
                method: 'POST',
                body: {
                    invitee_id: inviteeId,
                    ...(typeof message === 'string' && message.trim().length > 0 ? { message: message.trim() } : {}),
                },
            }),
            transformResponse: (response: unknown) => extractResponseData(response),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [
                { type: projectsTag, id: 'INVITABLE' },
                { type: projectsTag, id: arg.projectId },
                projectsTag,
            ],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    dispatch(recommendationsApi.util.invalidateTags([recommendationsTag, recommendationsSummaryTag]))
                } catch {
                    // ignore
                }
            },
        }),

        acceptProjectInvitation: builder.mutation<
            unknown,
            { invitationId: number; response_message?: string }
        >({
            query: ({ invitationId, response_message }) => ({
                url: `/users/me/invitations/projects/${invitationId}/accept`,
                method: 'POST',
                body: response_message != null ? { response_message } : {},
            }),
            transformResponse: (response: unknown) => extractResponseData(response),
            transformErrorResponse: extractResponseError,
            invalidatesTags: [projectsTag],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    dispatch(
                        notificationsApi.util.invalidateTags([
                            ...notificationFeedScopeTags,
                            { type: notificationsTag },
                        ])
                    )
                } catch {
                    // ignore
                }
            },
        }),

        declineProjectInvitation: builder.mutation<
            unknown,
            { invitationId: number; response_message?: string }
        >({
            query: ({ invitationId, response_message }) => ({
                url: `/users/me/invitations/projects/${invitationId}/decline`,
                method: 'POST',
                body: response_message != null ? { response_message } : {},
            }),
            transformResponse: (response: unknown) => extractResponseData(response),
            transformErrorResponse: extractResponseError,
            invalidatesTags: [projectsTag],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    dispatch(
                        notificationsApi.util.invalidateTags([
                            ...notificationFeedScopeTags,
                            { type: notificationsTag },
                        ])
                    )
                } catch {
                    // ignore
                }
            },
        }),

        getProjectTeamInvitations: builder.query<
            IProjectTeamInvitationResource[],
            { projectId: number; status?: string }
        >({
            query: ({ projectId, status }) => ({
                url: `/${urlApi.projects}/${projectId}/team-invitations`,
                params: status ? { status } : {},
            }),
            transformResponse: (response: unknown) => parseProjectTeamInvitationsList(response),
            transformErrorResponse: extractResponseError,
            providesTags: (result, error, arg) => [
                { type: projectsTag, id: `team-invitations:${arg.projectId}` },
            ],
        }),

        createProjectTeamInvitation: builder.mutation<
            IProjectTeamInvitationResource,
            { projectId: number; teamId: number; message?: string; expires_at?: string }
        >({
            query: ({ projectId, teamId, message, expires_at }) => ({
                url: `/${urlApi.projects}/${projectId}/team-invitations`,
                method: 'POST',
                body: {
                    team_id: teamId,
                    ...(typeof message === 'string' && message.trim().length > 0 ? { message: message.trim() } : {}),
                    ...(expires_at ? { expires_at } : {}),
                },
            }),
            transformResponse: (response: unknown) => {
                const parsed = parseProjectTeamInvitation(extractResponseData(response))
                if (!parsed) {
                    throw new Error('Invalid team invitation response')
                }
                return parsed
            },
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [
                { type: projectsTag, id: `team-invitations:${arg.projectId}` },
            ],
        }),

        cancelProjectTeamInvitation: builder.mutation<
            IProjectTeamInvitationResource,
            { projectId: number; invitationId: number }
        >({
            query: ({ projectId, invitationId }) => ({
                url: `/${urlApi.projects}/${projectId}/team-invitations/${invitationId}/cancel`,
                method: 'POST',
            }),
            transformResponse: (response: unknown) => {
                const parsed = parseProjectTeamInvitation(extractResponseData(response))
                if (!parsed) {
                    throw new Error('Invalid team invitation response')
                }
                return parsed
            },
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [
                { type: projectsTag, id: `team-invitations:${arg.projectId}` },
                { type: projectsTag, id: arg.projectId },
                projectsTag,
            ],
        }),

        removeProjectTeam: builder.mutation<void, { projectId: number; teamId: number; message?: string }>({
            query: ({ projectId, teamId, message }) => ({
                url: `/${urlApi.projects}/${projectId}/teams/${teamId}`,
                method: 'DELETE',
                ...(typeof message === 'string' && message.trim().length > 0 ? { body: { message: message.trim() } } : {}),
            }),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [
                { type: projectsTag, id: `id:${arg.projectId}` },
                { type: projectsTag, id: arg.projectId },
                { type: projectsTag, id: `team-invitations:${arg.projectId}` },
                projectsTag,
            ],
        }),

        leaveProject: builder.mutation<void, { projectId: number; message?: string }>({
            query: ({ projectId, message }) => ({
                url: `/${urlApi.projects}/${projectId}/leave`,
                method: 'POST',
                ...(typeof message === 'string' && message.trim().length > 0 ? { body: { message: message.trim() } } : {}),
            }),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }, projectsTag],
        }),

        getProject: builder.query<IProjectResponse, string | number>({
            query: (slugOrId) => ({
                url: `/${urlApi.projects}/${slugOrId}`,
            }),
            transformResponse: (response: unknown) => parseProjectResponse(response),
            transformErrorResponse: extractResponseError,
            providesTags: (result) =>
                result
                    ? [
                          { type: projectsTag, id: result.slug },
                          { type: projectsTag, id: `id:${result.id}` },
                      ]
                    : [],
        }),

        createProject: builder.mutation<IProjectResourceResponse, ICreateProject>({
            query: (body) => ({
                url: `/${urlApi.projects}`,
                method: 'POST',
                body,
            }),
            invalidatesTags: [projectsTag],
            transformErrorResponse: extractResponseError,
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    await invalidateLinkedSocialOrderProjects(
                        dispatch,
                        resolveSourceSocialOrderId(arg.social_order_id, data?.source_social_order),
                    )
                } catch {
                    // ignore
                }
            },
        }),

        updateProjectDescription: builder.mutation<
            IProjectResponse,
            { projectId: number; body: IUpdateProjectDescriptionBody }
        >({
            query: ({ projectId, body }) => ({
                url: `/${urlApi.projects}/${projectId}/update`,
                method: 'PUT',
                body,
            }),
            transformResponse: (response: unknown) => parseProjectResponse(response),
            invalidatesTags: [projectsTag],
            transformErrorResponse: extractResponseError,
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    await invalidateLinkedSocialOrderProjects(
                        dispatch,
                        resolveSourceSocialOrderId(null, data?.source_social_order),
                    )
                } catch {
                    // ignore
                }
            },
        }),

        deleteProject: builder.mutation<unknown, { projectId: number }>({
            query: ({ projectId }) => ({
                url: `/${urlApi.projects}/${projectId}/delete`,
                method: 'DELETE',
            }),
            transformResponse: (response: unknown) => extractResponseData(response),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }, projectsTag],
            async onQueryStarted(arg, { dispatch, getState, queryFulfilled }) {
                const socialOrderId = getCachedProjectSourceSocialOrderId(getState, arg.projectId)
                try {
                    await queryFulfilled
                    await invalidateLinkedSocialOrderProjects(dispatch, socialOrderId)
                } catch {
                    // ignore
                }
            },
        }),

        uploadProjectPreview: builder.mutation<MediaResourceLike, { projectId: number; file: File }>({
            query: ({ projectId, file }) => {
                const formData = new FormData()
                formData.append('image', file)
                return {
                    url: `/${urlApi.projects}/${projectId}/preview`,
                    method: 'POST',
                    body: formData,
                }
            },
            transformResponse: (response: unknown) => extractResponseData(response) as MediaResourceLike,
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }, projectsTag],
        }),

        deleteProjectPreview: builder.mutation<void, { projectId: number }>({
            query: ({ projectId }) => ({
                url: `/${urlApi.projects}/${projectId}/preview`,
                method: 'DELETE',
            }),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }, projectsTag],
        }),

        uploadProjectGallery: builder.mutation<MediaResourceLike[], { projectId: number; files: File[] }>({
            query: ({ projectId, files }) => {
                const formData = new FormData()
                files.forEach((file) => {
                    formData.append('images[]', file)
                })
                return {
                    url: `/${urlApi.projects}/${projectId}/gallery`,
                    method: 'POST',
                    body: formData,
                }
            },
            transformResponse: (response: unknown) => extractResponseData(response) as MediaResourceLike[],
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }, projectsTag],
        }),

        deleteProjectGalleryImage: builder.mutation<void, { projectId: number; mediaId: number }>({
            query: ({ projectId, mediaId }) => ({
                url: `/${urlApi.projects}/${projectId}/gallery/${mediaId}`,
                method: 'DELETE',
            }),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }, projectsTag],
        }),

        getProjectSteps: builder.query<IProjectStepResource[], { slugOrId: string | number }>({
            query: ({ slugOrId }) => ({
                url: `/${urlApi.projects}/${slugOrId}/steps`,
            }),
            transformResponse: (response: unknown) => extractResponseData(response) as IProjectStepResource[],
            transformErrorResponse: extractResponseError,
            providesTags: [projectsTag],
        }),

        createProjectStep: builder.mutation<IProjectStepResource, { projectId: number; body: ICreateProjectStepBody }>({
            query: ({ projectId, body }) => ({
                url: `/${urlApi.projects}/${projectId}/steps`,
                method: 'POST',
                body,
            }),
            transformResponse: (response: unknown) => extractResponseData(response) as IProjectStepResource,
            invalidatesTags: [projectsTag],
            transformErrorResponse: extractResponseError,
        }),

        deleteProjectStep: builder.mutation<unknown, { projectId: number; stepId: number }>({
            query: ({ projectId, stepId }) => ({
                url: `/${urlApi.projects}/${projectId}/steps/${stepId}/delete`,
                method: 'DELETE',
            }),
            invalidatesTags: [projectsTag],
            transformErrorResponse: extractResponseError,
        }),

        updateProjectStep: builder.mutation<unknown, { projectId: number; stepId: number; body: IUpdateProjectStepBody }>({
            query: ({ projectId, stepId, body }) => ({
                url: `/${urlApi.projects}/${projectId}/steps/${stepId}/update`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: [projectsTag],
            transformErrorResponse: extractResponseError,
        }),

        getComments: builder.query<TGetCommentsResponse, GetCommentsQueryArg>({
            query: ({ projectId, type, parentId, limit, cursor, sort }) => {
                const params: Record<string, string> = {}
                if (parentId != null) params.parent_id = String(parentId)
                if (limit != null) params.limit = String(limit)
                if (cursor != null && cursor !== '') params.cursor = String(cursor)
                if (parentId == null) {
                    appendCommentsSortQueryParams(params, sort ?? DEFAULT_COMMENTS_SORT)
                }
                return {
                    url: `/${urlApi.projects}/${projectId}/comments/${type ? type : ''}`,
                    ...(Object.keys(params).length > 0 ? { params } : {}),
                }
            },
            transformResponse: (response: unknown): TGetCommentsResponse => parseCommentsListResponse(response),
            transformErrorResponse: extractResponseError,
            providesTags: [projectsTag],
        }),

        /** Плоский тред под L2: сохраняем next_cursor без поломки общего списка комментариев. */
        getCommentThreadPage: builder.query<TCommentThreadPage, GetCommentThreadPageArg>({
            async queryFn(arg, _api, _extraOptions, baseQuery) {
                const params: Record<string, string> = {
                    parent_id: String(arg.parentId),
                }
                if (arg.cursor) params.cursor = arg.cursor
                if (arg.limit != null) params.limit = String(arg.limit)

                const res = await baseQuery({
                    url: `/${urlApi.projects}/${arg.projectId}/comments/${arg.type ? arg.type : ''}`,
                    params,
                })

                if (res.error) return { error: res.error }

                const { rows, metaRoot } = extractCommentThreadRowsAndMetaRoot(res.data)
                const items = rows.map((item) => normalizeCommentNode(item))
                const nextCursor = pickNextCursorFromBody(metaRoot)
                return { data: { items, nextCursor } }
            },
        }),

        addCommentToProject: builder.mutation<
            IGetCommentResponse,
            { projectId: number; type: string; body: IAddCommentToProject }
        >({
            query: ({ projectId, type, body }) => ({
                url: `/${urlApi.projects}/${projectId}/comments/${type ? type : ''}`,
                method: 'POST',
                body,
            }),
            transformResponse: (response: unknown) => normalizeCommentNode(extractResponseData(response)),
            transformErrorResponse: extractResponseError,
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    dispatch(
                        notificationsApi.util.invalidateTags([
                            ...notificationFeedScopeTags,
                            notificationsTag,
                        ])
                    )
                } catch {
                    // ignore
                }
            },
        }),

        updateComment: builder.mutation<IGetCommentResponse, { commentId: number; body: IUpdateCommentBody }>({
            query: ({ commentId, body }) => ({
                url: `/${urlApi.comments}/${commentId}/update`,
                method: 'PUT',
                body,
            }),
            transformResponse: (response: unknown) => normalizeCommentNode(extractResponseData(response)),
            transformErrorResponse: extractResponseError,
        }),

        setCommentReaction: builder.mutation<
            IGetCommentResponse,
            {
                commentId: number
                body: ISetCommentReactionBody
                /** Для патча getComments на странице проекта; иначе только ответ мутации. */
                cache?: {
                    projectId: number
                    type: string
                    sort?: CommentsSortUi
                    /** Якорь L2: патч getCommentThreadPage (первая страница без cursor). */
                    threadParentId?: number
                }
            }
        >({
            query: ({ commentId, body }) => ({
                url: `/${urlApi.comments}/${commentId}/reaction`,
                method: 'PUT',
                body,
            }),
            transformResponse: (response: unknown) => normalizeCommentNode(extractResponseData(response)),
            transformErrorResponse: extractResponseError,
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled
                    if (!arg.cache) return
                    const normalized = normalizeCommentNode(data)
                    dispatch(
                        projectsApi.util.updateQueryData(
                            'getComments',
                            {
                                projectId: arg.cache.projectId,
                                type: arg.cache.type,
                                limit: CURSOR_LIST_PAGE_SIZE,
                                sort: arg.cache.sort ?? DEFAULT_COMMENTS_SORT,
                            },
                            (draft) =>
                                patchCommentNodePreserveChildren(
                                    original(draft) ?? [...draft],
                                    arg.commentId,
                                    normalized
                                )
                        )
                    )
                    const threadParentId = arg.cache.threadParentId
                    if (threadParentId != null) {
                        dispatch(
                            projectsApi.util.updateQueryData(
                                'getCommentThreadPage',
                                {
                                    projectId: arg.cache.projectId,
                                    type: arg.cache.type,
                                    parentId: threadParentId,
                                    limit: CURSOR_LIST_PAGE_SIZE,
                                },
                                (draft) => {
                                    const i = draft.items.findIndex((it) => it.id === arg.commentId)
                                    if (i === -1) return
                                    const prev = draft.items[i]
                                    draft.items[i] = {
                                        ...prev,
                                        ...normalized,
                                        children: prev.children,
                                    }
                                }
                            )
                        )
                    }
                } catch {
                    /* no-op */
                }
            },
        }),

        getProjectImages: builder.query<{ avatarUrl: string | null; images: { url: string; mediaId?: number }[] }, { projectId: number }>({
            query: ({ projectId }) => ({
                url: `/${urlApi.projects}/${projectId}`,
            }),
            transformResponse: (response: unknown) => {
                const data: any = extractResponseData(response)
                const avatarUrl = getMediaSourceUrl(data?.image) || null
                const images = Array.isArray(data?.image_gallery)
                    ? (data.image_gallery as unknown[])
                        .flatMap((x: any) => {
                            const url = getMediaSourceUrl(x)
                            if (!url) return []
                            const mediaId = x?.id != null ? Number(x.id) : undefined
                            return [mediaId != null ? { url, mediaId } : { url }]
                        })
                    : []
                return { avatarUrl, images }
            },
            transformErrorResponse: extractResponseError,
            providesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }],
        }),

        getProjectWhoNeeded: builder.query<{ whoNeeded: { title: string; description: string }[] }, { projectId: number }>({
            async queryFn(arg, _api, _extra, baseQuery) {
                const res: any = await baseQuery({ url: `/${urlApi.projects}/${arg.projectId}/whoneeded` })
                if (res?.error?.status === 404) {
                    return { data: { whoNeeded: [] } }
                }
                if (res?.error) return { error: res.error }
                return { data: extractResponseData(res.data) as any }
            },
            providesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }],
        }),
        updateProjectWhoNeeded: builder.mutation<unknown, { projectId: number; body: Record<string, unknown> }>({
            query: ({ projectId, body }) => ({
                url: `/${urlApi.projects}/${projectId}/whoneeded`,
                method: 'PUT',
                body,
            }),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }, projectsTag],
        }),

        getProjectWhatNeeded: builder.query<{ whatNeeded: { title: string; description: string }[] }, { projectId: number }>({
            async queryFn(arg, _api, _extra, baseQuery) {
                const res: any = await baseQuery({ url: `/${urlApi.projects}/${arg.projectId}/whatneeded` })
                if (res?.error?.status === 404) {
                    return { data: { whatNeeded: [] } }
                }
                if (res?.error) return { error: res.error }
                return { data: extractResponseData(res.data) as any }
            },
            providesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }],
        }),
        updateProjectWhatNeeded: builder.mutation<unknown, { projectId: number; body: Record<string, unknown> }>({
            query: ({ projectId, body }) => ({
                url: `/${urlApi.projects}/${projectId}/whatneeded`,
                method: 'PUT',
                body,
            }),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }, projectsTag],
        }),

        getProjectNeedSlots: builder.query<IProjectNeedSlotResource[], { projectId: number }>({
            query: ({ projectId }) => ({
                url: `/${urlApi.projects}/${projectId}/need-slots`,
            }),
            transformResponse: (response: unknown) => {
                const data = extractResponseData(response)
                return Array.isArray(data) ? (data as IProjectNeedSlotResource[]) : []
            },
            transformErrorResponse: extractResponseError,
            providesTags: (result, error, arg) => [{ type: projectsTag, id: `need-slots:${arg.projectId}` }],
        }),
        createProjectNeedSlot: builder.mutation<IProjectNeedSlotResource, { projectId: number; body: IProjectNeedSlotMutationBody }>({
            query: ({ projectId, body }) => ({
                url: `/${urlApi.projects}/${projectId}/need-slots`,
                method: 'POST',
                body,
            }),
            transformResponse: (response: unknown) => extractResponseData(response) as IProjectNeedSlotResource,
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [
                { type: projectsTag, id: `need-slots:${arg.projectId}` },
                { type: projectsTag, id: arg.projectId },
            ],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    dispatch(recommendationsApi.util.invalidateTags([recommendationsTag, recommendationsSummaryTag]))
                } catch {
                    // no-op
                }
            },
        }),
        updateProjectNeedSlot: builder.mutation<IProjectNeedSlotResource, { projectId: number; slotId: number; body: IProjectNeedSlotMutationBody }>({
            query: ({ projectId, slotId, body }) => ({
                url: `/${urlApi.projects}/${projectId}/need-slots/${slotId}`,
                method: 'PATCH',
                body,
            }),
            transformResponse: (response: unknown) => extractResponseData(response) as IProjectNeedSlotResource,
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [
                { type: projectsTag, id: `need-slots:${arg.projectId}` },
                { type: projectsTag, id: arg.projectId },
            ],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    dispatch(recommendationsApi.util.invalidateTags([recommendationsTag, recommendationsSummaryTag]))
                } catch {
                    // no-op
                }
            },
        }),
        deleteProjectNeedSlot: builder.mutation<void, { projectId: number; slotId: number }>({
            query: ({ projectId, slotId }) => ({
                url: `/${urlApi.projects}/${projectId}/need-slots/${slotId}`,
                method: 'DELETE',
            }),
            transformResponse: () => undefined,
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [
                { type: projectsTag, id: `need-slots:${arg.projectId}` },
                { type: projectsTag, id: arg.projectId },
            ],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    dispatch(recommendationsApi.util.invalidateTags([recommendationsTag, recommendationsSummaryTag]))
                } catch {
                    // no-op
                }
            },
        }),

        getProjectWhoAvailable: builder.query<{ whoAvailable: { title: string; description: string }[] }, { projectId: number }>({
            async queryFn(arg, _api, _extra, baseQuery) {
                const res: any = await baseQuery({ url: `/${urlApi.projects}/${arg.projectId}/whoavailable` })
                if (res?.error?.status === 404) {
                    return { data: { whoAvailable: [] } }
                }
                if (res?.error) return { error: res.error }
                return { data: extractResponseData(res.data) as any }
            },
            providesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }],
        }),
        updateProjectWhoAvailable: builder.mutation<unknown, { projectId: number; body: Record<string, unknown> }>({
            query: ({ projectId, body }) => ({
                url: `/${urlApi.projects}/${projectId}/whoavailable`,
                method: 'PUT',
                body,
            }),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }, projectsTag],
        }),

        getProjectWhatAvailable: builder.query<{ whatAvailable: { title: string; description: string }[] }, { projectId: number }>({
            async queryFn(arg, _api, _extra, baseQuery) {
                const res: any = await baseQuery({ url: `/${urlApi.projects}/${arg.projectId}/whatavailable` })
                if (res?.error?.status === 404) {
                    return { data: { whatAvailable: [] } }
                }
                if (res?.error) return { error: res.error }
                return { data: extractResponseData(res.data) as any }
            },
            providesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }],
        }),
        updateProjectWhatAvailable: builder.mutation<unknown, { projectId: number; body: Record<string, unknown> }>({
            query: ({ projectId, body }) => ({
                url: `/${urlApi.projects}/${projectId}/whatavailable`,
                method: 'PUT',
                body,
            }),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }, projectsTag],
        }),

        getProjectMembers: builder.query<IProjectTeamMemberResource[], { projectId: number }>({
            query: ({ projectId }) => ({
                url: `/${urlApi.projects}/${projectId}/members`,
            }),
            transformResponse: (response: unknown) => {
                const payload = extractResponseData(response)
                if (!Array.isArray(payload)) return []
                return payload
                    .map((item: any) => {
                        const user = normalizeUserShort(item?.user)
                        if (!user || typeof user !== 'object') return null
                        const rawTeamRole = String(item?.team_role ?? 'member')
                        const teamRole = rawTeamRole === 'author'
                            ? 'author'
                            : (rawTeamRole === 'coauthor' ? 'coauthor' : 'member')
                        const fromRow = Number(item?.user_id)
                        const fromUser = typeof user.id === 'number' ? user.id : Number(user.id)
                        const userId = Number.isFinite(fromRow) && fromRow > 0
                            ? fromRow
                            : (Number.isFinite(fromUser) && fromUser > 0 ? fromUser : 0)
                        if (userId <= 0) return null
                        return {
                            user,
                            user_id: userId,
                            permission_role: item?.permission_role === 'admin' ? 'admin' : 'member',
                            team_role: teamRole,
                            role_label: item?.role_label != null ? String(item.role_label) : null,
                            is_featured: Boolean(item?.is_featured),
                            sort_order: Number(item?.sort_order ?? 0),
                            is_author: Boolean(item?.is_author),
                            source_team: normalizeSourceTeam(item?.source_team),
                        } satisfies IProjectTeamMemberResource
                    })
                    .filter((item: IProjectTeamMemberResource | null): item is IProjectTeamMemberResource => item !== null)
            },
            transformErrorResponse: extractResponseError,
            providesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }],
        }),
        removeProjectMember: builder.mutation<void, { projectId: number; userId: number }>({
            query: ({ projectId, userId }) => ({
                url: `/${urlApi.projects}/${projectId}/members/${userId}`,
                method: 'DELETE',
            }),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (_result, _error, arg) => [
                { type: projectsTag, id: arg.projectId },
                { type: projectsTag, id: `id:${arg.projectId}` },
                projectsTag,
            ],
        }),
        updateProjectTeam: builder.mutation<unknown, { projectId: number; body: IUpdateProjectTeamBody }>({
            query: ({ projectId, body }) => ({
                url: `/${urlApi.projects}/${projectId}/team`,
                method: 'PUT',
                body,
            }),
            transformResponse: (response: unknown) => extractResponseData(response),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [{ type: projectsTag, id: arg.projectId }, projectsTag],
        }),

        getProjectAdmins: builder.query<{ admin_user_ids: number[] }, { projectId: number }>({
            query: ({ projectId }) => ({
                url: `/${urlApi.projects}/${projectId}/admins`,
            }),
            transformResponse: (response: unknown) => {
                const payload = extractResponseData(response) as { admin_user_ids?: unknown }
                const ids = Array.isArray(payload?.admin_user_ids) ? payload.admin_user_ids : []
                return { admin_user_ids: ids.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0) }
            },
            transformErrorResponse: extractResponseError,
            providesTags: (result, error, arg) => [{ type: projectsTag, id: `admins:${arg.projectId}` }],
        }),

        updateProjectAdmins: builder.mutation<{ admin_user_ids: number[] }, { projectId: number; body: IUpdateProjectAdminsBody }>({
            query: ({ projectId, body }) => ({
                url: `/${urlApi.projects}/${projectId}/admins`,
                method: 'PUT',
                body,
            }),
            transformResponse: (response: unknown) => {
                const payload = extractResponseData(response) as { admin_user_ids?: unknown }
                const ids = Array.isArray(payload?.admin_user_ids) ? payload.admin_user_ids : []
                return { admin_user_ids: ids.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0) }
            },
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [
                { type: projectsTag, id: arg.projectId },
                { type: projectsTag, id: `admins:${arg.projectId}` },
                projectsTag,
            ],
        }),

        getProjectModerationUserCandidates: builder.query<
            IProjectModerationUserCandidatesResponse,
            { projectId: number }
        >({
            query: ({ projectId }) => ({
                url: `/${urlApi.projects}/${projectId}/moderation/user-candidates`,
            }),
            transformResponse: (response: unknown) => {
                const root = response as { meta?: { total?: unknown } } | null
                const payload = extractResponseData(response)
                const totalRaw = root?.meta?.total
                const itemsRaw = Array.isArray(payload) ? payload : []
                const items: IProjectModerationUserCandidate[] = []

                for (const item of itemsRaw as any[]) {
                    const user = normalizeUserShort(item)
                    if (!user) continue
                    const id = Number(item?.user_id ?? user.id)
                    if (!Number.isSafeInteger(id) || id <= 0) continue
                    items.push({
                        ...user,
                        id,
                        user_id: id,
                        slug: user.slug != null ? String(user.slug) : null,
                        fullname: user.fullname != null ? String(user.fullname) : null,
                        shortname: user.shortname != null ? String(user.shortname) : null,
                        deleted: Boolean(user.deleted),
                        restrictable: Boolean(item?.restrictable ?? true),
                        restrictable_reason:
                            typeof item?.restrictable_reason === 'string'
                                ? item.restrictable_reason
                                : null,
                    })
                }

                const total =
                    typeof totalRaw === 'number' && Number.isFinite(totalRaw) ? totalRaw : items.length
                return { items, total } satisfies IProjectModerationUserCandidatesResponse
            },
            transformErrorResponse: extractResponseError,
            providesTags: (result, error, arg) => [
                { type: projectsTag, id: `moderation-candidates:${arg.projectId}` },
            ],
        }),

        getProjectRestrictions: builder.query<IProjectUserRestrictionResource[], { projectId: number }>({
            query: ({ projectId }) => ({
                url: `/${urlApi.projects}/${projectId}/restrictions`,
            }),
            transformResponse: (response: unknown) => {
                const payload = extractResponseData(response)
                if (!Array.isArray(payload)) return []
                return payload
                    .map((item: any) => {
                        const user = normalizeUserShort(item?.user)
                        const createdBy = normalizeUserShort(item?.created_by)
                        if (!user) return null
                        const userId = Number(item?.user_id ?? user.id)
                        if (!Number.isSafeInteger(userId) || userId <= 0) return null
                        const level = item?.level === 'full' || item?.level === 'comments' ? item.level : 'comments'
                        return {
                            user,
                            user_id: userId,
                            level,
                            reason: item?.reason != null ? String(item.reason) : null,
                            created_at: String(item?.created_at ?? ''),
                            created_by: createdBy ?? user,
                        } satisfies IProjectUserRestrictionResource
                    })
                    .filter((item): item is IProjectUserRestrictionResource => item !== null)
            },
            transformErrorResponse: extractResponseError,
            providesTags: (result, error, arg) => [{ type: projectsTag, id: `restrictions:${arg.projectId}` }],
        }),

        createProjectRestriction: builder.mutation<
            IProjectUserRestrictionResource,
            { projectId: number; body: { user_id: number; level: ProjectUserRestrictionLevel; reason?: string } }
        >({
            query: ({ projectId, body }) => ({
                url: `/${urlApi.projects}/${projectId}/restrictions`,
                method: 'POST',
                body,
            }),
            transformResponse: (response: unknown) => {
                const item = extractResponseData(response) as any
                const user = normalizeUserShort(item?.user)
                const createdBy = normalizeUserShort(item?.created_by)
                if (!user) {
                    throw new Error('Invalid restriction response')
                }
                const userId = Number(item?.user_id ?? user.id)
                if (!Number.isSafeInteger(userId) || userId <= 0) {
                    throw new Error('Invalid restriction user_id')
                }
                const level = item?.level === 'full' || item?.level === 'comments' ? item.level : 'comments'
                return {
                    user,
                    user_id: userId,
                    level,
                    reason: item?.reason != null ? String(item.reason) : null,
                    created_at: String(item?.created_at ?? ''),
                    created_by: createdBy ?? user,
                }
            },
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [
                { type: projectsTag, id: arg.projectId },
                { type: projectsTag, id: `restrictions:${arg.projectId}` },
                projectsTag,
            ],
        }),

        deleteProjectRestriction: builder.mutation<void, { projectId: number; userId: number }>({
            query: ({ projectId, userId }) => ({
                url: `/${urlApi.projects}/${projectId}/restrictions/${userId}`,
                method: 'DELETE',
            }),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (result, error, arg) => [
                { type: projectsTag, id: arg.projectId },
                { type: projectsTag, id: `restrictions:${arg.projectId}` },
                projectsTag,
            ],
        }),

        getProjectTeamSpace: builder.query<IProjectTeamSpaceResource, { projectId: number }>({
            query: ({ projectId }) => ({
                url: `/${urlApi.projects}/${projectId}/team-space`,
            }),
            transformResponse: (response: unknown): IProjectTeamSpaceResource => {
                const payload = extractResponseData(response) as Record<string, unknown> | null
                const linksRaw = payload?.links
                const postsRaw = payload?.posts
                const links = Array.isArray(linksRaw)
                    ? linksRaw.filter((item): item is string => typeof item === 'string' && item.trim() !== '')
                    : []
                const posts: IProjectTeamBoardPostResource[] = Array.isArray(postsRaw)
                    ? postsRaw
                          .map((item: any) => normalizeTeamBoardPost(item))
                          .filter((item): item is IProjectTeamBoardPostResource => item !== null)
                    : []
                return { links, posts }
            },
            transformErrorResponse: extractResponseError,
            providesTags: (_result, _error, arg) => [
                { type: projectsTag, id: `team-space:${arg.projectId}` },
            ],
        }),

        updateProjectTeamSpaceLinks: builder.mutation<
            { links: string[] },
            { projectId: number; links: string[] }
        >({
            query: ({ projectId, links }) => ({
                url: `/${urlApi.projects}/${projectId}/team-space/links`,
                method: 'PUT',
                body: { links },
            }),
            transformResponse: (response: unknown) => {
                const payload = extractResponseData(response) as { links?: unknown } | null
                const links = Array.isArray(payload?.links)
                    ? payload!.links.filter((item): item is string => typeof item === 'string')
                    : []
                return { links }
            },
            transformErrorResponse: extractResponseError,
            invalidatesTags: (_result, _error, arg) => [
                { type: projectsTag, id: `team-space:${arg.projectId}` },
            ],
        }),

        upsertProjectTeamBoardPost: builder.mutation<
            IProjectTeamBoardPostResource,
            { projectId: number; body?: string; telegram?: string | null }
        >({
            query: ({ projectId, body, telegram }) => ({
                url: `/${urlApi.projects}/${projectId}/team-space/posts/me`,
                method: 'PUT',
                body: {
                    ...(body !== undefined ? { body } : {}),
                    ...(telegram !== undefined ? { telegram } : {}),
                },
            }),
            transformResponse: (response: unknown) => {
                const item = extractResponseData(response) as any
                const normalized = normalizeTeamBoardPost(item)
                if (!normalized) {
                    throw new Error('Invalid team board post response')
                }
                return normalized
            },
            transformErrorResponse: extractResponseError,
            invalidatesTags: (_result, _error, arg) => [
                { type: projectsTag, id: `team-space:${arg.projectId}` },
            ],
        }),

        deleteOwnProjectTeamBoardPost: builder.mutation<void, { projectId: number }>({
            query: ({ projectId }) => ({
                url: `/${urlApi.projects}/${projectId}/team-space/posts/me`,
                method: 'DELETE',
            }),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (_result, _error, arg) => [
                { type: projectsTag, id: `team-space:${arg.projectId}` },
            ],
        }),

        deleteProjectTeamBoardPost: builder.mutation<void, { projectId: number; userId: number }>({
            query: ({ projectId, userId }) => ({
                url: `/${urlApi.projects}/${projectId}/team-space/posts/${userId}`,
                method: 'DELETE',
            }),
            transformErrorResponse: extractResponseError,
            invalidatesTags: (_result, _error, arg) => [
                { type: projectsTag, id: `team-space:${arg.projectId}` },
            ],
        }),

        deleteComment: builder.mutation<void, { commentId: number }>({
            query: ({ commentId }) => ({
                url: `/comments/${commentId}/delete`,
                method: 'DELETE',
            }),
            transformErrorResponse: extractResponseError,
        }),
    }),
})

export const {
    useGetProjectsQuery,
    useGetProjectsPageQuery,
    useLazyGetProjectsPageQuery,
    useGetProjectsStatsQuery,
    useGetMyProjectsQuery,
    useLazyGetMyProjectsPageQuery,
    useGetSubscribedProjectsQuery,
    useGetSubscribedProjectsPageQuery,
    useLazyGetSubscribedProjectsPageQuery,
    useGetParticipatingProjectsQuery,
    useGetParticipatingProjectsPageQuery,
    useLazyGetParticipatingProjectsPageQuery,
    useLazyGetUserParticipatingProjectsPageQuery,
    useTrackProjectViewMutation,
    useSubscribeProjectMutation,
    useUnsubscribeProjectMutation,
    useLikeProjectMutation,
    useUnlikeProjectMutation,
    useGetProjectLikesQuery,
    useLazyGetProjectLikesQuery,
    useCreateProjectJoinRequestMutation,
    useApproveProjectJoinRequestMutation,
    useRejectProjectJoinRequestMutation,
    useGetInvitableProjectsQuery,
    useCreateProjectInvitationMutation,
    useAcceptProjectInvitationMutation,
    useDeclineProjectInvitationMutation,
    useGetProjectTeamInvitationsQuery,
    useCreateProjectTeamInvitationMutation,
    useCancelProjectTeamInvitationMutation,
    useRemoveProjectTeamMutation,
    useLeaveProjectMutation,
    useGetProjectQuery,
    useCreateProjectMutation,
    useUpdateProjectDescriptionMutation,
    useDeleteProjectMutation,
    useUploadProjectPreviewMutation,
    useDeleteProjectPreviewMutation,
    useUploadProjectGalleryMutation,
    useDeleteProjectGalleryImageMutation,
    useGetProjectStepsQuery,
    useCreateProjectStepMutation,
    useUpdateProjectStepMutation,
    useDeleteProjectStepMutation,
    useGetCommentsQuery,
    useLazyGetCommentsQuery,
    useLazyGetCommentThreadPageQuery,
    useAddCommentToProjectMutation,
    useUpdateCommentMutation,
    useSetCommentReactionMutation,
    useGetProjectImagesQuery,
    useLazyGetProjectImagesQuery,
    useGetProjectWhoNeededQuery,
    useLazyGetProjectWhoNeededQuery,
    useUpdateProjectWhoNeededMutation,
    useGetProjectWhatNeededQuery,
    useLazyGetProjectWhatNeededQuery,
    useUpdateProjectWhatNeededMutation,
    useGetProjectNeedSlotsQuery,
    useCreateProjectNeedSlotMutation,
    useUpdateProjectNeedSlotMutation,
    useDeleteProjectNeedSlotMutation,
    useGetProjectWhoAvailableQuery,
    useLazyGetProjectWhoAvailableQuery,
    useUpdateProjectWhoAvailableMutation,
    useGetProjectWhatAvailableQuery,
    useLazyGetProjectWhatAvailableQuery,
    useUpdateProjectWhatAvailableMutation,
    useGetProjectMembersQuery,
    useRemoveProjectMemberMutation,
    useUpdateProjectTeamMutation,
    useGetProjectAdminsQuery,
    useUpdateProjectAdminsMutation,
    useGetProjectModerationUserCandidatesQuery,
    useGetProjectRestrictionsQuery,
    useCreateProjectRestrictionMutation,
    useDeleteProjectRestrictionMutation,
    useGetProjectTeamSpaceQuery,
    useUpdateProjectTeamSpaceLinksMutation,
    useUpsertProjectTeamBoardPostMutation,
    useDeleteOwnProjectTeamBoardPostMutation,
    useDeleteProjectTeamBoardPostMutation,
    useDeleteCommentMutation,
} = projectsApi
